/**
 * Attribution for events discarded by a Sentry EVENT PROCESSOR (BAL-959).
 *
 * WHY THIS IS A SEPARATE PATH FROM `onIgnoredDrop`. Sentry records a discarded
 * event under one of several mutually exclusive reasons, and the two that
 * matter here are produced at different points in `Client._processEvent`
 * (@sentry/core 10.70.0, `build/cjs/client.js`):
 *
 *   - `_prepareEvent` resolves null  → `recordDroppedEvent('event_processor')`
 *   - `beforeSend`    returns null   → `recordDroppedEvent('before_send')`
 *
 * BAL-856 / BAL-891 instrumented the SECOND one only. So a burst reported by
 * `stats_v2` as `client_discard / event_processor` was invisible to the
 * `sentry_event_dropped` sink BY CONSTRUCTION — not because the sink was
 * broken. Four bursts (2026-07-29, 2026-08-09/10, 2026-08-19, 2026-08-20) were
 * each re-diagnosed from scratch and each closed as unattributable, because
 * "the drop telemetry returned zero rows" was read as evidence about the wrong
 * stage. This module closes that gap so the fifth burst is attributable.
 *
 * WHERE THE DROP COMES FROM. `setupIntegration` turns every integration's
 * `processEvent` method into a client event processor tagged with the
 * integration's name (`integration.js`: `Object.assign(cb, { id: name })` →
 * `client.addEventProcessor`). Both live suspects are exactly that shape:
 * `Dedupe.processEvent` returns null for a byte-identical repeat, and
 * `InboundFilters` returns null for a filtered message. Wrapping the client's
 * processor list therefore names the culprit directly.
 *
 * ...AND WHERE ELSE IT COMES FROM (the blind spot this module used to have).
 * The client's list is NOT the whole chain. `prepareEvent` builds the chain as
 * `[...client.getEventProcessors(), ...getCombinedScopeData(...).eventProcessors]`
 * (`utils/prepareEvent.js:34-47`), and `getCombinedScopeData` merges the
 * GLOBAL, ISOLATION and CURRENT scopes (`utils/scopeData.js:67-72`). A scope
 * processor that returns falsy is billed `event_processor` exactly like a
 * client one (`client.js:587`), yet `client.getEventProcessors()` returns only
 * `this._eventProcessors` (`client.js:275-277`) and never sees it.
 *
 * That was not theoretical. @sentry/nextjs registers two of them unconditionally,
 * and both are burst candidates:
 *
 *   - BROWSER: `filterNextRedirectError` (id `NextRedirectErrorFilter`), dropping
 *     `NEXT_REDIRECT`. Registered via `core.addEventProcessor`, which resolves to
 *     `getIsolationScope().addEventProcessor` (`exports.js:122-123`) — the
 *     ISOLATION scope, NOT the global one. Verified at runtime against the real
 *     SDK: after `init()`, the isolation scope holds `NextRedirectErrorFilter`
 *     and the global scope holds nothing. Instrumenting only the global scope
 *     would therefore have missed the single most likely browser culprit while
 *     reporting itself as armed — the same looks-covered-but-is-not failure this
 *     module exists to remove.
 *   - SERVER: `DropReactControlFlowErrors` on `getGlobalScope()`
 *     (`server/index.js:145-164`), dropping React postpone and Suspense
 *     control-flow errors.
 *
 * So all three scopes are wrapped, and they are RESOLVED PER EVENT rather than
 * captured at install time: `getCurrentScope()` returns a different object
 * inside every `withScope`, and `Scope.clone` copies `_eventProcessors` into a
 * NEW array (`scope.js:55`), so a snapshot taken at install goes stale on the
 * first fork. `getScopeData()` hands out the scope's live array by reference
 * (`scope.js:438`), so wrapping is an in-place index assignment — verified at
 * runtime by mutating index 0 and reading the id back through the accessor.
 *
 * RESIDUAL GAPS — what "wrapped" does NOT extend to.
 *
 * The per-event re-scan covers a LONG-LIVED array: a processor pushed onto the
 * global or isolation scope is wrapped on event N and covered from N+1 on. It
 * cannot cover an EPHEMERAL one, and wrapping harder would not change that.
 *
 *   1. A processor added inside `withScope`. `prepareEvent` has already spread
 *      the combined chain (`utils/prepareEvent.js:41-45`) before the beacon —
 *      which sits inside that spread — runs, so an in-place wrap lands on an
 *      array the callback discards when it returns. The fork is uncovered on
 *      the only event it will ever see, and there is no next event to catch up
 *      on. This is live, not hypothetical: `captureConsoleIntegration` is
 *      exactly this shape (`integrations/captureconsole.js:42-49`) and is
 *      enabled in the browser.
 *   2. A processor passed as the `captureException(err, cb)` callback — the
 *      shape `integrations/supabase.js:305-312` uses. It lands on `finalScope`,
 *      a clone minted INSIDE `_prepareEvent` (`utils/prepareEvent.js:168-175`).
 *      `getCurrentScope()` never returns that object, so it is invisible at
 *      every point in this instrument's lifetime, re-scan included.
 *
 * Neither is hidden behind a count that claims otherwise. `processorCount` and
 * `scopeProcessorCount` count only processors that were ALREADY wrapped when
 * this event's chain was spread — genuinely covered — while one wrapped for
 * the first time during this event lands in `wrappedThisEventCount` instead. A
 * fork processor is wrapped for the first time on every event it ever sees, so
 * it can never be counted as covered, and a persistently non-zero
 * `wrappedThisEventCount` is itself the signal that forks are in play.
 *
 * WHAT COUNTS AS A DROP. `_notifyEventProcessors` halts the chain on ANY FALSY
 * return, not on `null` alone (`eventProcessors.js:18` —
 * `if (!event || !processor) return event`), and `prepareEvent` then passes the
 * result through `normalizeEvent`, which maps every falsy to `null`
 * (`utils/prepareEvent.js:124`) before `client.js:587` tests it. So under the
 * default `normalizeDepth: 3` an `undefined` return is billed as an
 * `event_processor` discard exactly like a `null` one. Matching only strict
 * `null` here would therefore leave a blind spot in the one place this module
 * exists to have none.
 *
 * PURELY ADDITIVE. Each wrapper calls the original processor and returns its
 * result unchanged; nothing is filtered, reordered or suppressed. That is
 * deliberate — the capture behaviour under investigation must not change while
 * we are measuring it.
 */
import {
  extractRoute,
  isLocalOrigin,
  MESSAGE_PREFIX_LENGTH,
  normalizeRoutePath,
  redactAndTruncate,
} from './drop-telemetry';
import { isPreviewEnvironment } from './preview-environment';

/**
 * Telemetry payload for an event discarded by an event processor.
 *
 * Mirrors `IgnoredDropInfo` field-for-field where the concepts overlap, so the
 * two halves land in one queryable `sentry_event_dropped` table rather than in
 * two shapes that have to be unioned by hand.
 */
export interface ProcessorDropInfo {
  /** Integration name (`Dedupe`, `InboundFilters`) or `processor-<index>`. */
  processor: string;
  /** `error` | `transaction` | … — Sentry's own event type. */
  eventType: string;
  /** First 120 chars of the event message, PII-redacted. */
  messagePrefix: string;
  /** `TypeError`, `ChunkLoadError`, … — empty when the event carries none. */
  exceptionType: string;
  /** Request route — normalized, no query string, no opaque segments. */
  route: string;
  /** `production` | `preview` | `development` | … */
  environment: string;
  /**
   * True on the LAST row a signature — or the whole pageload — is allowed to
   * emit. Read it as "the counts at and below this row are FLOORS, not totals".
   *
   * Set by EITHER ceiling, and that is the point. The per-signature cap is the
   * one that actually fires in a BAL-959 burst: a single looping error caps at
   * 20 rows while `totalReports` never comes near the 100 pageload ceiling. A
   * marker derived from the pageload ceiling alone left all 20 of those rows
   * reading `false`, which downstream reads as "20 drops, fully attributed" —
   * reproducing one level down the exact failure this field exists to prevent:
   * a burst that looks fully attributed but is not.
   */
  budgetExhausted: boolean;
  /**
   * True when the PER-SIGNATURE cap is the ceiling that fired.
   *
   * Separates the two truncations, which demand different readings. This one
   * means "this signature stopped being counted; every OTHER signature on the
   * pageload is still complete". `budgetExhausted` without it means the
   * pageload itself stopped, so nothing after that row was recorded at all.
   * A query that reads only `budgetExhausted` is never WRONG — it just cannot
   * tell the two apart, which is the safe direction for a flag to fail in.
   */
  signatureExhausted: boolean;
}

export type ProcessorDropHandler = (drop: ProcessorDropInfo) => void;

/**
 * Emitted once per pageload, on the first ERROR event to enter the chain.
 *
 * This is the liveness half, and it is the whole reason a future zero-row
 * result can be trusted. Without it, "the sink returned no rows" has two
 * indistinguishable causes: nothing was dropped, or the sink was never wired.
 * That ambiguity is what made four consecutive bursts unattributable. With it,
 * `armed = 0` means the instrumentation is not deployed or not reached, and
 * `armed > 0 AND drops = 0` means the sink ran and every processor it COVERS
 * kept the event.
 *
 * COVERS is the load-bearing word, and it is why there are five counts rather
 * than two. Each field exists because a reading that was available without it
 * was wrong:
 *
 *   - `processorCount` + `scopeProcessorCount` — what this event was covered
 *     for. Both count wrappers, not array entries.
 *   - `wrappedThisEventCount` — what was in the chain and NOT covered on this
 *     event. Non-zero on a fork-local processor, on every event it ever sees.
 *   - `scopesRequested` vs `scopesResolved` — separates "the scopes were read
 *     and hold no processors" from "the scopes could not be read at all".
 *
 * So the strong statement a zero-row result supports is bounded: the drop came
 * from neither the client list nor from any scope processor that was covered.
 * It says nothing about a fork-local or `finalScope` processor — see the
 * module docstring's RESIDUAL GAPS — which is exactly what
 * `wrappedThisEventCount` is there to make visible instead of assumed away.
 */
export interface SinkArmedInfo {
  /**
   * CLIENT event processors COVERED for this event: already wrapped when
   * `prepareEvent` spread the chain. Excludes the beacon, which is the
   * instrumentation rather than something it watches.
   *
   * Covered, not present. A processor pushed onto the client list after init
   * IS wrapped by the beacon's re-scan, but that wrap lands on the array the
   * NEXT event runs — so on this event it is unwrapped, and counting it here
   * would claim coverage the sink does not have. It appears in
   * `wrappedThisEventCount` instead and moves into this count from the next
   * event on.
   *
   * The array length is deliberately not the number: it also counts
   * non-function entries, which are never wrapped and never drop anything.
   */
  processorCount: number;
  /**
   * SCOPE-level event processors COVERED for this event, across the global,
   * isolation and current scopes, deduplicated by processor identity.
   *
   * Deliberately a SECOND field rather than a widening of `processorCount`.
   * The two arrays fail independently — a client-list regression and a
   * scope-list regression have different causes and different fixes — and one
   * summed number cannot tell them apart. Splitting them also keeps
   * `processor_count` meaning the same thing it meant in every PostHog row
   * written before scope coverage existed, so the historical series stays
   * comparable instead of silently changing units mid-flight.
   *
   * Deduplicated because one scope object reachable through two resolvers is
   * ONE processor in the chain, not two; summing `list.length` across the
   * three sources counted it once per source.
   *
   * `0` here is a real reading only alongside a non-zero `scopesResolved` — it
   * then means the scopes were read and held no covered processors. With
   * `scopesResolved: 0` it means nothing was read at all, which is a different
   * finding with a different fix.
   */
  scopeProcessorCount: number;
  /**
   * Processors — client and scope alike — wrapped for the FIRST time during
   * this event, and therefore NOT covered on it.
   *
   * A long-lived array drains this to zero: whatever is wrapped now is covered
   * from the next event on. An ephemeral one never does. A processor added
   * inside `withScope` lives for exactly one event, so it is wrapped for the
   * first time every time and is counted here every time — which is the only
   * honest place to count it, since the wrap lands on an array the callback
   * discards. A `wrappedThisEventCount` that stays non-zero across pageloads
   * therefore reads as "fork-local processors are in play", not as "the sink
   * is still warming up".
   */
  wrappedThisEventCount: number;
  /**
   * Scope sources this install accounts for.
   *
   * Not simply `scopes.length` as passed by the caller: an install whose scope
   * processors are already owned by an earlier sink accounts for none of them,
   * and reports `0` here rather than claiming sources whose drops report
   * somewhere else.
   */
  scopesRequested: number;
  /**
   * How many of those sources yielded a processor array on this event.
   *
   * Exists because `scopesResolved < scopesRequested` and
   * `scopeProcessorCount: 0` are different findings that used to look
   * identical. A source is skipped when its resolver throws, returns nothing,
   * exposes no `getScopeData`, or hands back a non-array — four failure modes
   * that all reduced to a silent zero. A `getScopeData` renamed upstream would
   * have degraded the whole scope half to no coverage while the beacon still
   * armed and still reported `scopeProcessorCount: 0`, which reads as "the
   * scopes are empty". `3 requested, 0 resolved` cannot be read that way.
   */
  scopesResolved: number;
  environment: string;
}

export type SinkArmedHandler = (info: SinkArmedInfo) => void;

/**
 * Cap on reports per processor+signature per pageload.
 *
 * Matches `MAX_DROP_REPORTS_PER_PATTERN` in `sentry.client.config.ts` on
 * purpose: the two halves write the same PostHog event, so a burst must not be
 * throttled generously on one path and tightly on the other. The bound is what
 * keeps the sink from becoming the volume problem it exists to measure — the
 * 2026-08-19 burst was ~1700 discards in one hour, and this caps a single
 * pathological tab's contribution at 20 rows per distinct drop signature.
 */
export const MAX_PROCESSOR_DROP_REPORTS_PER_KEY = 20;

/**
 * Hard ceiling on reports per pageload, across ALL signatures.
 *
 * The per-signature cap alone does NOT bound total volume, and assuming it
 * does is the easy mistake here. It bounds one repeated error; a burst of
 * 1700 DISTINCT messages has 1700 distinct signatures and would have produced
 * ~1700 PostHog writes — turning a diagnostic into the same quota problem it
 * exists to diagnose. This ceiling is what actually holds the guarantee.
 *
 * 100 leaves room for five fully-attributed signatures at the per-key cap,
 * which is far more than is needed to name a culprit, while bounding a
 * pathological tab. It also bounds the throttle Map: entries are only created
 * for reports that are actually emitted, so the Map cannot exceed this.
 */
export const MAX_PROCESSOR_DROP_REPORTS_PER_PAGELOAD = 100;

/** Max chars of exception type forwarded. Class names are short by nature. */
const EXCEPTION_TYPE_LENGTH = 60;

/** Marker so a processor is never double-wrapped across lazy re-scans. */
const WRAPPED_FLAG = '__balleeDropInstrumented';

/** `id` of the head-of-chain beacon. Also the double-install sentinel. */
const BEACON_ID = 'BalleeDropSinkBeacon';

interface MinimalEvent {
  type?: string;
  message?: string;
  exception?: { values?: Array<{ type?: string; value?: string }> };
  request?: { url?: string };
  tags?: Record<string, unknown>;
}

/**
 * `undefined` is in this union deliberately: the SDK's own chain halts on any
 * falsy return, so a processor CAN drop an event without returning `null`, and
 * a type that denied it would make the guard below look redundant.
 */
type ProcessorResult =
  | MinimalEvent
  | null
  | undefined
  | PromiseLike<MinimalEvent | null | undefined>;

interface EventProcessorLike {
  (event: MinimalEvent, hint: unknown): ProcessorResult;
  id?: string;
  [WRAPPED_FLAG]?: boolean;
}

interface ClientLike {
  getEventProcessors?: () => EventProcessorLike[];
}

/**
 * The three scopes `getCombinedScopeData` merges, in the order it merges them
 * (`utils/scopeData.js:67-72`). The label is part of the reported processor
 * name, so a drop says WHICH scope it came from, not merely that it came from
 * one.
 */
export type ScopeKind = 'global' | 'isolation' | 'current';

/**
 * A scope whose processors join the chain, supplied as a RESOLVER.
 *
 * A resolver, not a scope instance, because `getCurrentScope()` returns a
 * different object inside every `withScope` and `Scope.clone` copies
 * `_eventProcessors` into a new array — so a scope captured at install time
 * stops being the one the next event runs against.
 */
export interface ScopeSource {
  kind: ScopeKind;
  resolve: () => unknown;
}

interface ScopeLike {
  getScopeData?: () => { eventProcessors?: unknown } | undefined;
}

/**
 * What an install call actually did.
 *
 * A boolean could not say this. It conflated "this call installed the sink"
 * with "an earlier call already covers this client", and the second answer was
 * returned in a case where the call had installed NOTHING — see the scope
 * ownership note in `instrumentEventProcessorDrops`. A caller that logs or
 * asserts on the outcome needs the two apart.
 *
 *   - `installed`       — this call unshifted the beacon and wrapped the chain.
 *   - `already-covered` — this exact client already carries a beacon; nothing
 *                         was changed, and drops still report to the first sink.
 *   - `not-wired`       — the client exposed no `getEventProcessors`. Read this
 *                         as "no telemetry", NEVER as "nothing was dropped".
 */
export type ProcessorDropInstallOutcome =
  | 'installed'
  | 'already-covered'
  | 'not-wired';

interface ResolvedScope {
  kind: ScopeKind;
  list: EventProcessorLike[];
}

function isThenable(
  value: unknown,
): value is PromiseLike<MinimalEvent | null | undefined> {
  return (
    typeof (value as { then?: unknown } | null | undefined)?.then === 'function'
  );
}

/** Build the sanitized payload. Same hygiene helper as the `beforeSend` half. */
export function buildProcessorDropInfo(args: {
  processor: string;
  event: MinimalEvent;
  environment: string;
  budgetExhausted?: boolean;
  signatureExhausted?: boolean;
}): ProcessorDropInfo {
  const {
    processor,
    event,
    environment,
    budgetExhausted = false,
    signatureExhausted = false,
  } = args;
  const exception = event.exception?.values?.[0];

  // `event.message` is set for console captures and `captureMessage`;
  // `exception.value` for thrown errors. Either can carry arbitrary upstream
  // text, so both go through the shared redact-then-truncate helper.
  const rawMessage = event.message ?? exception?.value ?? '';

  return {
    processor,
    eventType: event.type ?? 'error',
    messagePrefix: redactAndTruncate(String(rawMessage)),
    exceptionType: redactAndTruncate(
      String(exception?.type ?? ''),
      EXCEPTION_TYPE_LENGTH,
    ),
    route: resolveRoute(event),
    environment,
    budgetExhausted,
    signatureExhausted,
  };
}

function resolveRoute(event: MinimalEvent): string {
  if (event.request?.url) {
    return extractRoute(event.request.url, event.tags?.routePath);
  }
  // Browser events frequently carry no `request.url` before the SDK's own
  // processors have run, and this instrumentation sits at the HEAD of the
  // chain by design, so the live location is the more reliable source here.
  if (typeof window !== 'undefined' && window.location?.pathname) {
    return normalizeRoutePath(window.location.pathname);
  }
  return 'unknown';
}

/**
 * Wrap every client AND scope event processor so a falsy return is attributed.
 *
 * See `ProcessorDropInstallOutcome` for what the return value distinguishes.
 * The client list is the gate: it is where the head-of-chain beacon lives, and
 * without it there is nowhere to hang the per-event re-scan that keeps the
 * scope lists wrapped.
 */
export function instrumentEventProcessorDrops(args: {
  client: unknown;
  environment: string;
  onProcessorDrop: ProcessorDropHandler;
  onSinkArmed?: SinkArmedHandler;
  /**
   * Scope-level processor sources. Omit for client-only coverage; production
   * passes the global, isolation and current scopes. See the module docstring
   * for why omitting them leaves `NextRedirectErrorFilter` invisible.
   */
  scopes?: ScopeSource[];
}): ProcessorDropInstallOutcome {
  const { client, environment, onProcessorDrop, onSinkArmed, scopes } = args;

  const exposed = (client as ClientLike | undefined)?.getEventProcessors?.();
  if (!Array.isArray(exposed)) return 'not-wired';
  // Re-bind with an explicit type: TypeScript does not carry an `Array.isArray`
  // narrowing into the closures below, and every consumer here is a closure.
  const processors: EventProcessorLike[] = exposed;

  /**
   * Resolve scope processor arrays, fresh, on every call.
   *
   * Never cached. `getCurrentScope()` returns a different object inside every
   * `withScope`, and `Scope.clone` copies `_eventProcessors` into a NEW array
   * (`scope.js:55`), so a cached array is the previous scope's — wrapping it
   * would report nothing while looking installed. A source is SKIPPED, never
   * faked, when its resolver throws, returns nothing, exposes no
   * `getScopeData`, or hands back a non-array. How many were skipped is
   * reported: the count of resolved sources reaches the beacon as
   * `scopesResolved`, so a shape change upstream cannot masquerade as an empty
   * scope. Before that field existed it did exactly that — renaming
   * `getScopeData` degraded the whole scope half to no coverage while the
   * beacon still armed and still reported `scopeProcessorCount: 0`.
   */
  function resolveScopeList(sources: ScopeSource[]): ResolvedScope[] {
    const resolved: ResolvedScope[] = [];
    for (const source of sources) {
      let list: unknown;
      try {
        list = (source.resolve() as ScopeLike | undefined)?.getScopeData?.()
          ?.eventProcessors;
      } catch {
        // A resolver that throws must not take the event path down with it.
        continue;
      }
      if (Array.isArray(list)) {
        resolved.push({
          kind: source.kind,
          list: list as EventProcessorLike[],
        });
      }
    }
    return resolved;
  }

  // A second install against the SAME client would find every processor
  // already flagged, so `wrapAll` would no-op — but it would still unshift a
  // SECOND beacon whose wrappers close over the first call's `report`. That
  // beacon arms and can never receive a drop, manufacturing precisely the
  // `armed > 0, drops = 0` reading this module exists to make trustworthy.
  // Unreachable through `initializeSentryBrowserClient` today; the guard is one
  // line and removes the failure mode rather than relying on that staying true.
  if (processors.some((p) => p?.id === BEACON_ID)) return 'already-covered';

  // The scopes need their OWN guard, and folding it into the one above was a
  // regression. The scopes are process-global while the beacon sentinel lives
  // in ONE client's list, so a second install against a DIFFERENT client
  // passes the beacon check and then finds the global/isolation processors
  // already flagged by the first sink. It cannot re-wrap them — `wrap` returns
  // a flagged processor untouched — so those drops keep reporting into the
  // first sink no matter what this call does.
  //
  // Treating that as proof of a prior install for the WHOLE call returned
  // success while wrapping nothing at all: client B lost its beacon AND its
  // client-list coverage, including `Dedupe` and `InboundFilters`, the original
  // suspects. Only the scopes are the first sink's; the client list is this
  // one's. So this install takes the client list and DISOWNS the scopes, which
  // is why `scopesRequested` reports 0 for it — a sink must not account for
  // processors whose drops land in someone else's sink.
  const scopesOwnedElsewhere = resolveScopeList(scopes ?? []).some((scope) =>
    scope.list.some((p) => typeof p === 'function' && p[WRAPPED_FLAG]),
  );
  const scopeSources: ScopeSource[] = scopesOwnedElsewhere
    ? []
    : (scopes ?? []);

  function resolveScopes(): ResolvedScope[] {
    return resolveScopeList(scopeSources);
  }

  const reportCounts = new Map<string, number>();
  let totalReports = 0;
  let armed = false;
  let reporting = false;

  /**
   * Mirror the unconditional `beforeSend` drops (BAL-791 preview, and local
   * dev) so the sink's population matches the production signal being chased.
   * Those events are discarded milliseconds later anyway; reporting them would
   * spend quota on rows that can never explain a production burst, and would
   * pipe local dev noise into the shared table.
   *
   * The dev-origin test is `isLocalOrigin`, the SAME predicate the `beforeSend`
   * halves use (BAL-953). It previously compared two exact hostnames, which
   * left this sink writing real error text from a laptop on `0.0.0.0`, a LAN
   * address, a `.local` name or a tunnel — and this module writes the SAME
   * `sentry_event_dropped` event as the other two halves, so a gap here makes
   * the table safe on some rows and raw on others, which is the one property
   * `drop-telemetry.ts` exists to guarantee. Found by the BAL-953 review.
   *
   * This sink REFUSES the row rather than suppressing its text, because that
   * is already its policy for localhost and preview: an `event_processor` drop
   * from a dev machine cannot explain a production burst either way.
   */
  function shouldReport(): boolean {
    if (isPreviewEnvironment(environment)) return false;
    return !isLocalOrigin(
      typeof window !== 'undefined' ? window.location?.hostname : undefined,
    );
  }

  function report(processor: string, event: MinimalEvent): void {
    if (!shouldReport()) return;
    // BOTH ceilings are checked before anything is built. Past either one this
    // must cost nothing — not the redaction pass, not the URL parse — because
    // it runs inside the event pipeline during a burst. The per-signature cap
    // is the one that fires in practice: one looping error caps at 20 while
    // `totalReports` never approaches 100, so a pageload-only pre-check left
    // the remaining ~1680 events of a 1700-event burst each paying for a
    // payload that was then thrown away.
    if (totalReports >= MAX_PROCESSOR_DROP_REPORTS_PER_PAGELOAD) return;
    // `onProcessorDrop` runs a PostHog capture from inside the processor
    // chain. If that capture ever fails in a way Sentry captures, the new event
    // re-enters this same chain. Bounded in practice by the caps above, but the
    // guard is one boolean and removes the cycle outright.
    if (reporting) return;
    reporting = true;
    try {
      // Key on the drop SIGNATURE, not just the processor: one looping error
      // must not exhaust the budget for a different error dropped by the same
      // integration, or the second one becomes invisible exactly when a burst
      // is underway.
      //
      // Derived from RAW fields on purpose, so the cap can be tested before the
      // payload is built. Raw keying groups less aggressively than the redacted
      // payload would — two messages differing only in an embedded id are two
      // keys here and one after redaction — so more distinct signatures each
      // get their own 20-row budget. That is deliberate: the pageload ceiling
      // below is what bounds TOTAL volume, so nothing gets louder, while the
      // extra keys surface variants of a looping error instead of collapsing
      // them. The alternative (redacted keying plus an exhausted-raw-key set)
      // keeps grouping identical but only short-circuits byte-identical
      // repeats, leaving the variable-id burst — the shape redaction exists
      // for — paying full price on every event.
      const exception = event.exception?.values?.[0];
      const rawMessage = String(event.message ?? exception?.value ?? '');
      const key = `${processor}|${String(exception?.type ?? '')}|${rawMessage.slice(
        0,
        MESSAGE_PREFIX_LENGTH,
      )}`;

      const count = reportCounts.get(key) ?? 0;
      if (count >= MAX_PROCESSOR_DROP_REPORTS_PER_KEY) return;

      const signatureExhausted =
        count + 1 >= MAX_PROCESSOR_DROP_REPORTS_PER_KEY;
      const pageloadExhausted =
        totalReports + 1 >= MAX_PROCESSOR_DROP_REPORTS_PER_PAGELOAD;

      const info = buildProcessorDropInfo({
        processor,
        event,
        environment,
        budgetExhausted: signatureExhausted || pageloadExhausted,
        signatureExhausted,
      });

      reportCounts.set(key, count + 1);
      totalReports += 1;
      onProcessorDrop(info);
    } catch {
      // Telemetry must never break the drop path.
    } finally {
      reporting = false;
    }
  }

  function wrap(
    processor: EventProcessorLike,
    fallbackName: string,
  ): EventProcessorLike {
    if (processor[WRAPPED_FLAG]) return processor;

    // `id` is set by `setupIntegration` to the integration name, and
    // @sentry/nextjs sets it explicitly on both of its scope processors
    // (`NextRedirectErrorFilter`, `DropReactControlFlowErrors`). A processor
    // registered directly via `addEventProcessor` may have none, so fall back
    // to its position — an unnamed culprit is still a located one. The caller
    // supplies the fallback because a bare `processor-<index>` would be
    // ambiguous once four different arrays are wrapped: index 2 of the client
    // list and index 2 of the isolation scope are different processors.
    const name = processor.id ?? fallbackName;

    const wrapped: EventProcessorLike = (event, hint) => {
      const result = processor(event, hint);
      // Falsy, not `=== null`. The SDK halts the chain on any falsy return and
      // normalizes it to `null` before recording the discard, so `undefined`
      // is a real `event_processor` drop. The value is returned UNCHANGED —
      // this observes, it does not coerce.
      if (!result) {
        report(name, event);
        return result;
      }
      if (isThenable(result)) {
        // Chain the ORIGINAL thenable rather than wrapping it in
        // `Promise.resolve`. The SDK resolves its internal `SyncPromise`
        // synchronously; adopting it into a native Promise would defer
        // resolution by a microtask and make this instrumentation
        // observable in event ordering. Telemetry must not shift timing.
        return result.then((final) => {
          if (!final) report(name, event);
          return final;
        });
      }
      return result;
    };

    wrapped.id = processor.id;
    wrapped[WRAPPED_FLAG] = true;
    return wrapped;
  }

  function wrapList(list: EventProcessorLike[], prefix: string): void {
    for (let i = 0; i < list.length; i++) {
      const processor = list[i];
      if (typeof processor === 'function') {
        list[i] = wrap(processor, `${prefix}${i}`);
      }
    }
  }

  function wrapAll(resolvedScopes: ResolvedScope[]): void {
    wrapList(processors, 'processor-');
    for (const scope of resolvedScopes) {
      // In-place index assignment on the array `getScopeData()` handed out.
      // That array IS the scope's own `_eventProcessors` (`scope.js:438`), so
      // the mutation is what the next event runs — verified at runtime against
      // the real SDK, not inferred from the source alone.
      wrapList(scope.list, `scope-${scope.kind}-processor-`);
    }
  }

  function hasUnwrapped(list: EventProcessorLike[]): boolean {
    return list.some((p) => typeof p === 'function' && !p[WRAPPED_FLAG]);
  }

  interface CoverageCensus {
    clientCovered: number;
    scopeCovered: number;
    wrappedThisEvent: number;
  }

  /**
   * Count what is COVERED for the event now in flight, and what is not.
   *
   * Must be taken BEFORE the re-wrap pass, because that pass is the only moment
   * the two populations are distinguishable. `prepareEvent` spreads the combined
   * chain before running it (`utils/prepareEvent.js:41-45`), so a wrap applied
   * now lands on the array the NEXT event runs: a processor ALREADY carrying the
   * flag was wrapped on a previous event and is covered here, while one being
   * wrapped for the first time right now is not.
   *
   * That distinction is the whole reason a fork-local processor can be counted
   * honestly. A processor added inside `withScope` exists for exactly one event,
   * so it is unwrapped every time the census sees it and lands in
   * `wrappedThisEvent` every time — it can never be miscounted as covered, and
   * no next-event catch-up is being claimed on its behalf.
   *
   * Counted by walking entries rather than reading `list.length`, which
   * over-counted on two inputs: a non-function entry, which is never wrapped and
   * never drops anything, and one scope object reachable through two resolvers,
   * which is one processor in the chain and was counted once per source.
   */
  function takeCoverageCensus(resolvedScopes: ResolvedScope[]): CoverageCensus {
    let clientCovered = 0;
    let scopeCovered = 0;
    let wrappedThisEvent = 0;

    for (const processor of processors) {
      // The beacon is the instrumentation, not something it watches.
      if (typeof processor !== 'function' || processor.id === BEACON_ID) {
        continue;
      }
      if (processor[WRAPPED_FLAG]) clientCovered += 1;
      else wrappedThisEvent += 1;
    }

    const seen = new Set<EventProcessorLike>();
    for (const scope of resolvedScopes) {
      for (const processor of scope.list) {
        if (typeof processor !== 'function' || seen.has(processor)) continue;
        seen.add(processor);
        if (processor[WRAPPED_FLAG]) scopeCovered += 1;
        else wrappedThisEvent += 1;
      }
    }

    return { clientCovered, scopeCovered, wrappedThisEvent };
  }

  /** Sentry tags a transaction with `type: 'transaction'`; an error has none. */
  function isErrorEvent(event: MinimalEvent): boolean {
    return event.type === undefined;
  }

  /**
   * Head-of-chain beacon. Two jobs, both of which need to run before any
   * other processor sees the event:
   *
   *   1. Re-wrap the list when any entry is unwrapped. Integrations added after
   *      init (`addIntegration`, lazy route instrumentation) push unwrapped
   *      processors onto the same array, and an unwrapped processor is a
   *      silent blind spot — the exact failure mode this module exists to
   *      remove. `_prepareEvent` spreads the array into a fresh list before
   *      running it, so mutating here affects the NEXT event, never this one.
   *
   *      The trigger is "is anything unwrapped", not "has the length changed".
   *      A length comparison cannot see an add-plus-remove or an in-place
   *      replacement, and either leaves a PERMANENT blind spot. Scanning ~15
   *      entries for a flag is cheaper than the mistake.
   *
   *   2. Emit the liveness signal once per pageload — on an ERROR event only.
   *      A sampled transaction travels this same chain, and at
   *      `tracesSampleRate: 0.1` that is ~10% of ALL pageviews. Arming on one
   *      would make the beacon a pageview firehose whose rows say nothing about
   *      error handling, and would make `armed > 0 AND drops = 0` trivially
   *      true almost everywhere — destroying the single reading the beacon
   *      exists to support. Gating on errors keeps that reading meaningful:
   *      "errors flowed through this pageload's chain, and none were dropped by
   *      a client processor".
   *
   *      The re-wrap above is deliberately NOT gated the same way: a processor
   *      added after init is a blind spot from the moment it appears, and
   *      waiting for the first error to notice would leave that window open for
   *      exactly the events that matter.
   */
  const beacon: EventProcessorLike = (event) => {
    // Resolved here, on every event, for the same reason the install-time call
    // is not reused: inside a `withScope` the current scope is a fresh clone
    // with its own array, and a stale handle would leave that fork unwrapped.
    const resolvedScopes = resolveScopes();
    // Taken BEFORE the re-wrap below — see `takeCoverageCensus` — and only when
    // it will be used, so every event in a burst after the first skips the walk.
    const census =
      !armed && isErrorEvent(event) ? takeCoverageCensus(resolvedScopes) : null;
    if (
      hasUnwrapped(processors) ||
      resolvedScopes.some((scope) => hasUnwrapped(scope.list))
    ) {
      wrapAll(resolvedScopes);
    }
    if (census) {
      armed = true;
      if (onSinkArmed && shouldReport()) {
        try {
          onSinkArmed({
            processorCount: census.clientCovered,
            scopeProcessorCount: census.scopeCovered,
            wrappedThisEventCount: census.wrappedThisEvent,
            // What this install ACCOUNTS for, versus what it could read on this
            // event. Equal is the healthy reading; `requested > resolved` names
            // a scope source that could not be read, which is a different
            // finding from a scope that was read and held nothing.
            scopesRequested: scopeSources.length,
            scopesResolved: resolvedScopes.length,
            environment,
          });
        } catch {
          // Telemetry must never break the event path.
        }
      }
    }
    return event;
  };
  beacon.id = BEACON_ID;
  beacon[WRAPPED_FLAG] = true;

  // Unshift BEFORE the first wrap pass so a fallback `processor-<index>` name
  // is the position the event actually travels through. Wrapping first named
  // the originals 0..n-1 while their real positions became 1..n, and every
  // processor wrapped later used post-unshift indices — so the same list
  // carried two incompatible numbering schemes.
  processors.unshift(beacon);
  wrapAll(resolveScopes());

  return 'installed';
}
